import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import VoiceSampler from './VoiceSampler';
import { TURNSTILE_SITE_KEY, loadTurnstileScript } from '../lib/turnstile';

const { FiZap, FiEdit3, FiShield, FiCheckCircle, FiAlertTriangle, FiLoader, FiArrowRight, FiFeather, FiHeadphones } = FiIcons;

const API = '/voice-api/v1';

const VOICES = [
  { id: 'austen', author: 'Jane Austen', book: 'Pride and Prejudice', tag: 'Ironic wit', photo: '/authors/austen.jpg' },
  { id: 'dickens', author: 'Charles Dickens', book: 'A Tale of Two Cities', tag: 'Victorian', photo: '/authors/dickens.jpg' },
  { id: 'tolstoy', author: 'Leo Tolstoy', book: 'Anna Karenina', tag: 'Russian realist', photo: '/authors/tolstoy.jpg' },
  { id: 'dorian', author: 'Oscar Wilde', book: 'The Picture of Dorian Gray', tag: 'Gothic wit', photo: '/authors/wilde.jpg' },
  { id: 'alice', author: 'Lewis Carroll', book: 'Alice in Wonderland', tag: 'Whimsical', photo: '/authors/carroll.jpg' },
  { id: 'vitaly', author: 'Vitaly Magidov', book: 'A Ukrainian Orphan', tag: 'Memoir · real author', photo: '/authors/vitaly.jpg' },
];

const SAMPLE = 'I was tired and wanted to go home. The party was boring and there was nobody interesting to talk to. Everyone kept laughing at jokes I could not hear, holding drinks they barely touched, leaning into conversations that meant nothing to me. I stood by the window and counted the minutes, wondering why I had come at all, and whether anyone would notice if I simply walked out into the cold.';
const MIN_WORDS = 50;
const wordCount = (t) => (t.trim() ? t.trim().split(/\s+/).length : 0);

const Gauge = ({ value, label, good, sub }) => {
  const v = value == null ? 0 : value;
  const color = good ? (v >= 60 ? 'text-green-600' : 'text-amber-500') : (v <= 40 ? 'text-green-600' : 'text-red-500');
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 text-center">
      <div className={`text-3xl font-bold tabular-nums ${color}`}>{value == null ? '—' : `${v}%`}</div>
      <div className="text-xs font-semibold text-gray-700 mt-1">{label}</div>
      {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
    </div>
  );
};

const VoiceLab = ({ showAudiobookSampler = true }) => {
  const [voice, setVoice] = useState('dorian');
  const [text, setText] = useState(SAMPLE);
  const [output, setOutput] = useState('');
  const [outKind, setOutKind] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [model, setModel] = useState('claude');
  const [models, setModels] = useState([{ id: 'claude', label: 'Claude Sonnet' }]);
  const [compareRows, setCompareRows] = useState(null);
  const [grammar, setGrammar] = useState(null);
  const cfTokenRef = useRef(null);
  const widgetElRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/models`).then((r) => r.json()).then((m) => { if (Array.isArray(m) && m.length) setModels(m); }).catch(() => {});
  }, []);

  // Invisible Turnstile widget backing the voice-api's bot check (CT-26705). We don't gate the
  // UI on it — the backend only asks for it once per IP per 30 minutes (guard()'s pass cache) —
  // we just keep a fresh token ready to send whenever it's requested.
  useEffect(() => {
    let cancelled = false;
    loadTurnstileScript().then(() => {
      if (cancelled || !window.turnstile || !widgetElRef.current || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(widgetElRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        size: 'flexible',
        callback: (token) => { cfTokenRef.current = token; },
        'expired-callback': () => { cfTokenRef.current = null; },
        'error-callback': () => { cfTokenRef.current = null; },
      });
    }).catch(() => { /* verification unavailable — calls still work if the backend hasn't asked for it yet */ });
    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current) window.turnstile.remove(widgetIdRef.current);
    };
  }, []);

  const authHeaders = () => (cfTokenRef.current ? { 'X-Turnstile-Token': cfTokenRef.current } : {});

  const current = VOICES.find((v) => v.id === voice);
  const words = wordCount(text);
  const tooShort = words < MIN_WORDS;

  const call = async (kind) => {
    setErr(''); setBusy(kind);
    const isAnalyze = kind === 'analyze' || kind === 'analyzeInput';
    if (!isAnalyze) setAnalysis(null);
    const src = kind === 'analyze' ? (output || text) : text;   // 'analyzeInput' scores the user's own writing
    const path = isAnalyze ? 'analyze' : kind;
    try {
      const r = await fetch(`${API}/${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ voice, text: src, model }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Something went wrong.');
      if (isAnalyze) { setAnalysis({ ...d, _ownWriting: kind === 'analyzeInput' }); }
      else { setOutput((d.rewritten || d.continuation || '').trim()); setOutKind(kind); }
    } catch (e) {
      setErr(e.message || 'The voice engine is busy — try again in a moment.');
    } finally { setBusy(''); }
  };

  const checkGrammar = async () => {
    setErr(''); setBusy('grammar'); setGrammar(null);
    try {
      const r = await fetch(`${API}/grammar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ text: output || text }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Something went wrong.');
      setGrammar(d);
    } catch (e) {
      setErr(e.message || 'The grammar service is busy — try again in a moment.');
    } finally { setBusy(''); }
  };

  const compareModels = async () => {
    setErr(''); setBusy('compare'); setCompareRows(null);
    try {
      const r = await fetch(`${API}/compare`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ voice, text }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Something went wrong.');
      setCompareRows(d.results || []);
    } catch (e) {
      setErr(e.message || 'The voice engine is busy — try again in a moment.');
    } finally { setBusy(''); }
  };

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Turnstile mounts here — invisible/managed, so this is usually empty space (Cloudflare
          decides whether a visible challenge is ever needed) */}
      <div ref={widgetElRef} className="mt-2" />
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-primary-700 bg-primary-50 px-3 py-1 rounded-full mb-3">
          <SafeIcon icon={FiFeather} className="w-4 h-4" /> Live voice engine — real AI, no signup
        </div>
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Write it in <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">someone&apos;s voice</span> — then prove it&apos;s real</h2>
        <p className="text-gray-600 mt-2 max-w-2xl mx-auto">Paste up to 5,000 characters of your own writing, rewrite it in a voice we studied from the real book, then run our AI analyzer to see how human it reads and how closely it matches that voice.</p>
      </div>

      {showAudiobookSampler && (
        <>
          {/* Audiobook narration tie-in */}
          <div className="mb-6 rounded-2xl border border-primary-100 bg-gradient-to-r from-primary-50 to-secondary-50 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiHeadphones} className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-gray-900">The same voice picker powers your audiobook narration</div>
              <p className="text-sm text-gray-600">Choose a narration voice below and hear how each one reads your book before you master it to ACX spec.</p>
            </div>
            <Link to="/audiobook/voices" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 whitespace-nowrap flex-shrink-0">
              Browse narration voices <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
            </Link>
          </div>

          {/* Playable audiobook narration samples */}
          <div className="mb-6">
            <VoiceSampler subtitle="Real sample narration — press play to hear how each voice reads a book." />
          </div>
        </>
      )}

      {/* Voice picker — writing voices + audiobook narration */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
        {VOICES.map((v) => (
          <button key={v.id} onClick={() => { setVoice(v.id); setAnalysis(null); }}
            className={`flex items-center gap-3 rounded-xl border-2 p-3 text-left transition-all ${voice === v.id ? 'border-primary-500 bg-primary-50/50 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
            <img src={v.photo} alt={v.author} loading="lazy" className="w-11 h-11 rounded-full object-cover shadow ring-2 ring-secondary-100 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-bold text-gray-900 truncate">{v.author}</div>
              <div className="text-[11px] text-gray-500 truncate">{v.tag}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Input */}
        <div className="bg-parchment-50 rounded-2xl border border-parchment-200 shadow-sm overflow-hidden flex flex-col">
          <div className="px-4 py-2 border-b border-parchment-200 bg-white/60 text-xs font-semibold text-gray-500 flex items-center justify-between">
            <span className="flex items-center gap-1.5">
              Your words
              <button type="button" onClick={() => setText("The kitchen still smelled of bread when I came home that winter, though no one had baked in weeks. I set my bag down by the door and listened. The house held its breath the way it always did before someone said the thing that could not be unsaid. I was nine, and already I knew the sound of bad news arriving on quiet feet.")}
                className="text-primary-600 hover:text-primary-800 underline decoration-dotted underline-offset-2">— or load an example</button>
            </span>
            <span className={`tabular-nums ${tooShort ? 'text-amber-600' : 'text-gray-400'}`}>{words} words{tooShort ? ` · need ${MIN_WORDS}+` : ''}</span>
          </div>
          <textarea value={text}
            onChange={(e) => { setText(e.target.value.slice(0, 5000)); }}
            onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 700) + 'px'; }}
            spellCheck={true} lang="en" maxLength={5000}
            className="w-full min-h-[14rem] resize-y bg-transparent px-5 py-4 text-gray-800 leading-relaxed focus:outline-none flex-1 overflow-y-auto"
            style={{ fontFamily: '"Fraunces", Georgia, serif', fontSize: '1.05rem' }}
            placeholder="Type or paste up to 5,000 characters of your own writing… (the box grows as you write)" />
          <div className="flex items-center gap-2 px-4 pt-3 bg-white/60">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">AI model</span>
            <select value={model} onChange={(e) => setModel(e.target.value)} disabled={!!busy}
              className="text-sm border border-gray-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:border-primary-400">
              {models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <button onClick={compareModels} disabled={!!busy || tooShort}
              className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 hover:text-primary-800 disabled:opacity-40">
              <SafeIcon icon={busy === 'compare' ? FiLoader : FiZap} className={`w-3.5 h-3.5 ${busy === 'compare' ? 'animate-spin' : ''}`} />
              Compare all AI models
            </button>
          </div>
          <div className="flex flex-wrap gap-2 px-4 py-3 border-t border-parchment-200 bg-white/60 mt-2">
            <button onClick={() => call('rewrite')} disabled={!!busy || tooShort}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white text-sm bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 disabled:opacity-50">
              <SafeIcon icon={busy === 'rewrite' ? FiLoader : FiEdit3} className={`w-4 h-4 ${busy === 'rewrite' ? 'animate-spin' : ''}`} />
              Rewrite in {current.author.split(' ')[0]}&apos;s voice
            </button>
            <button onClick={() => call('continue')} disabled={!!busy || tooShort}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-gray-700 text-sm bg-white border border-gray-200 hover:border-gray-300 disabled:opacity-50">
              <SafeIcon icon={busy === 'continue' ? FiLoader : FiZap} className={`w-4 h-4 ${busy === 'continue' ? 'animate-spin' : ''}`} />
              Continue it
            </button>
            <button onClick={() => call('analyzeInput')} disabled={!!busy || tooShort}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-gray-700 text-sm bg-white border border-gray-200 hover:border-gray-300 disabled:opacity-50">
              <SafeIcon icon={busy === 'analyzeInput' ? FiLoader : FiShield} className={`w-4 h-4 ${busy === 'analyzeInput' ? 'animate-spin' : ''}`} />
              Do I write like {current.author.split(' ').slice(-1)[0]}?
            </button>
            <button onClick={checkGrammar} disabled={!!busy || !text.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-gray-700 text-sm bg-white border border-gray-200 hover:border-gray-300 disabled:opacity-50">
              <SafeIcon icon={busy === 'grammar' ? FiLoader : FiCheckCircle} className={`w-4 h-4 ${busy === 'grammar' ? 'animate-spin' : ''}`} />
              Check grammar &amp; style
            </button>
          </div>
        </div>

        {/* Output */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
          <div className="px-4 py-2 border-b border-gray-100 bg-gradient-to-r from-primary-50 to-secondary-50 text-xs font-semibold text-gray-600 flex items-center justify-between">
            <span>In {current.author}&apos;s voice</span>
            {output && <span className="text-[10px] uppercase tracking-wide text-primary-500">{outKind === 'continue' ? 'continued' : 'rewritten'}</span>}
          </div>
          <div className="px-5 py-4 flex-1 min-h-[10rem] max-h-[28rem] overflow-y-auto">
            {output ? (
              <p className="text-gray-800 leading-relaxed whitespace-pre-wrap" style={{ fontFamily: '"Fraunces", Georgia, serif', fontSize: '1.05rem' }}>{output}</p>
            ) : (
              <p className="text-gray-400 text-sm italic flex items-center h-full">Your rewritten passage will appear here.</p>
            )}
          </div>
          {output && (
            <div className="px-4 py-3 border-t border-gray-100">
              <button onClick={() => call('analyze', 'analyze')} disabled={!!busy}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-white text-sm bg-gray-900 hover:bg-gray-800 disabled:opacity-50">
                <SafeIcon icon={busy === 'analyze' ? FiLoader : FiShield} className={`w-4 h-4 ${busy === 'analyze' ? 'animate-spin' : ''}`} />
                {busy === 'analyze' ? 'Analyzing…' : 'Analyze with AI — is it human? is it this voice?'}
              </button>
            </div>
          )}
        </div>
      </div>

      {err && (
        <div className="mt-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">
          <SafeIcon icon={FiAlertTriangle} className="w-4 h-4" /> {err}
        </div>
      )}

      {/* Analysis */}
      <AnimatePresence>
        {analysis && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="mt-5 bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            {analysis._ownWriting ? (
              <div className="mb-4 text-center">
                <div className="text-3xl font-bold text-gray-900">You write <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">{analysis.voiceMatch ?? '—'}%</span> like {current.author}</div>
                <div className="text-sm text-gray-500 mt-1">Your own words, measured against {current.author}&apos;s style — plus whether they read human or AI.</div>
              </div>
            ) : (
              <div className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2"><SafeIcon icon={FiShield} className="w-4 h-4 text-primary-600" /> AI authenticity analysis</div>
            )}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <Gauge value={analysis.humanScore} label="Reads as human" good sub="higher is better" />
              <Gauge value={analysis.aiLikelihood} label="AI-written likelihood" sub="lower is better" />
              <Gauge value={analysis.voiceMatch} label={`Matches ${current.author.split(' ')[0]}`} good sub="voice fidelity" />
            </div>
            {Array.isArray(analysis.perSentence) && analysis.perSentence.length > 0 && (
              <div className="mb-3">
                <div className="text-xs font-semibold text-gray-500 mb-2">Sentence-by-sentence — <span className="text-green-600">human</span> vs <span className="text-red-500">AI-like</span></div>
                <p className="leading-relaxed">
                  {analysis.perSentence.map((s, i) => (
                    <span key={i} className={`${s.label === 'ai' ? 'bg-red-50 text-red-700 border-b-2 border-red-300' : 'bg-green-50 text-green-800'} rounded px-1 mr-1 inline`}>{s.text} </span>
                  ))}
                </p>
              </div>
            )}
            {analysis.summary && <p className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2"><span className="font-semibold">Verdict:</span> {analysis.summary}</p>}
            <p className="text-[11px] text-gray-400 mt-3">Same engine that scores your chapters before you publish. In the studio it&apos;s tuned to <em>your</em> voice, not a famous author&apos;s.</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Grammar & style results */}
      <AnimatePresence>
        {grammar && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="mt-5 bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-bold text-gray-900">Grammar &amp; style</span>
              <span className="ml-auto text-sm font-bold tabular-nums">{grammar.total === 0 ? <span className="text-green-600">Clean — 0 issues</span> : <span className="text-amber-600">{grammar.total} to review</span>}</span>
            </div>
            {grammar.total > 0 && (
              <>
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.entries(grammar.byType || {}).map(([k, n]) => (
                    <span key={k} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{n} {k}</span>
                  ))}
                </div>
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {grammar.issues.map((it, i) => (
                    <div key={i} className="text-sm border-b border-gray-100 pb-2 last:border-0">
                      <div className="text-gray-700">{it.message}</div>
                      {it.suggestions && it.suggestions.length > 0 && (
                        <div className="text-xs text-gray-500 mt-0.5">Suggestion: <span className="text-primary-600 font-medium">{it.suggestions.join(' · ')}</span></div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
            <p className="text-[11px] text-gray-400 mt-3">Private grammar &amp; style engine — your text never leaves the studio.</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Model comparison — the marketing money shot */}
      <AnimatePresence>
        {compareRows && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="mt-5 bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            <div className="text-sm font-bold text-gray-900 mb-1">We ran your passage through every AI — scored against {current.author}&apos;s voice</div>
            <div className="text-xs text-gray-500 mb-4">Same author voice, same analyzer. Higher voice-match + lower AI-likelihood wins.</div>
            <div className="space-y-2">
              {compareRows.map((r, i) => (
                <div key={r.model} className={`rounded-xl border p-4 ${i === 0 ? 'border-primary-300 bg-primary-50/40' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {i === 0 && <span className="text-[10px] font-bold uppercase tracking-wide text-white bg-primary-500 px-2 py-0.5 rounded-full">Best match</span>}
                    <span className="font-bold text-gray-900">{r.label}</span>
                    {r.error ? <span className="ml-auto text-xs text-red-400">{r.error}</span> : (
                      <span className="ml-auto flex gap-3 text-xs tabular-nums">
                        <span className="text-primary-600 font-bold">{r.voiceMatch ?? '—'}% voice</span>
                        <span className="text-green-600">{r.humanScore ?? '—'}% human</span>
                        <span className="text-gray-400">{r.aiLikelihood ?? '—'}% AI</span>
                      </span>
                    )}
                  </div>
                  {r.rewritten && <p className="text-sm text-gray-600 italic line-clamp-3">&ldquo;{r.rewritten}&rdquo;</p>}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-3">Pick the winning model in the dropdown above to keep writing with it.</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Conversion CTA — fires once they've felt it work */}
      <AnimatePresence>
        {output && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="mt-5 rounded-2xl bg-gradient-to-r from-primary-600 to-secondary-600 p-6 sm:p-8 text-center text-white shadow-xl">
            <div className="text-xl sm:text-2xl font-bold mb-2">That was {current.author}&apos;s voice. Imagine it tuned to <span className="underline decoration-white/40">yours.</span></div>
            <p className="opacity-90 mb-5 max-w-2xl mx-auto">ManuscriptSmith learns <em>your</em> voice from your own pages — so every chapter sounds like you wrote it, and passes AI detection. Get started, no card.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/signup" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-white text-primary-700 font-bold rounded-lg hover:bg-gray-50">
                Train it on my voice <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </Link>
              <Link to="/voice-dna" className="inline-flex items-center justify-center px-6 py-3.5 bg-white/15 border border-white/40 text-white font-semibold rounded-lg hover:bg-white/25">
                How Voice DNA works
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="text-center text-[11px] text-gray-400 mt-4">Live demo · real Claude-powered analysis · rate-limited. Your text isn&apos;t stored.</p>
    </section>
  );
};

export default VoiceLab;
